import type {
	AccountAgreementApiResponse,
	AccountApiResponse,
	AdditionalPhone,
	MappedAccountAgreement,
	MappedAccountData,
	MappedAccountDiscount,
	PhoneNumberApiResponse,
	PortalAccountPermanentDiscountApiResponse,
	PortalAccountTemporaryDiscountApiResponse,
	SignupPackage, SignupPackageResponse
} from '@/components/account/types'
import { formatDate, MDY2 } from '@/utils/formatDate'

const mapTemporaryDiscounts = (discounts: PortalAccountTemporaryDiscountApiResponse[]): MappedAccountDiscount[] => {
	// filter out discounts that have not been applied to an invoice and are not repetitions
	return [ ...discounts ]
		?.filter(discount => !discount.invoiceId && discount.currentNumber === discount.maxNumber)
		.map(discount => ({
			id: discount.contractPregeneratedFeeId,
			amount: Math.abs(discount.charge),
			type: discount.type,
			isPermanent: false,
			name: discount.type,
			monthsRemaining: discounts?.filter(d => d.type === discount.type)?.filter(d => !d.invoiceId)?.length
		})) || []
}

const mapPermanentDiscounts = (discounts: PortalAccountPermanentDiscountApiResponse[]): MappedAccountDiscount[] => {
	return discounts?.map(({
		discountAmount,
		type,
		contractPermanentDiscountId,
		discountName
	}) => ({
		id: contractPermanentDiscountId,
		amount: discountAmount,
		type,
		name: discountName,
		isPermanent: true
	})) || []
}

const mapAccountAgreements = (agreements: AccountAgreementApiResponse[]): MappedAccountAgreement[] => {
	return agreements?.map(({
		id,
		agreementSignatureDate,
		agreementSigner,
		agreementSnapshot,
		agreementType,
		lastModified
	}) => ({
		id,
		signatureDate: agreementSignatureDate ? new Date(agreementSignatureDate) : null,
		signer: agreementSigner,
		snapshot: JSON.parse(agreementSnapshot),
		type: agreementType,
		lastModified: lastModified ? new Date(lastModified) : null
	})) || []
}

const mapAccount = (account: AccountApiResponse): MappedAccountData => {
	const {
		accountId,
		// accountName,
		// accountType,
		accountStatus,
		serviceDetails,
		activatedTime,
		accountCreatedTime,
		// taxExempt,
		address,
		owner,
		zone,
		prospectConversionSteps,
		prospectInterestBillablesSnapshot,
		portalAccountServices: accountServices,
		moveInDate,
		ignoreOverdue,
		stickyNote,
		accountContract,
		equipment,
		isDelinquent,
		hasInvoices,
		tags,
		accountAgreements,
		hasDiscount
	} = account || {}
	return {
		accountId,
		contactId: owner?.contactId || 0,
		title: owner?.title || '',
		firstName: owner?.first || '',
		middleName: owner?.middle || '',
		lastName: owner?.last || '',
		suffix: owner?.suffix || '',
		address: address?.address1 || '',
		address2: address?.address2 || '',
		city: address?.city?.name || '',
		state: address?.state.name || '',
		zip: address?.postalCode || '',
		phone: findPrimaryPhone(owner?.phones)?.number,
		extension: findPrimaryPhone(owner?.phones)?.extension,
		email: owner?.email || '',
		isActive: accountStatus?.status === 'Active',
		area: zone?.name || '',
		activatedTime: activatedTime ? new Date(activatedTime) : null,
		createdTime: accountCreatedTime ? new Date(accountCreatedTime) : null,
		serviceDetails,
		discounts: [ ...mapTemporaryDiscounts(serviceDetails?.contractPregeneratedFee), ...mapPermanentDiscounts(serviceDetails?.contractPermanentDiscount) ],
		accountStatus: accountStatus?.status,
		prospectConversionSteps,
		additionalPhones: mapAdditionalPhones(owner?.phones),
		signupPackage: mapSignupPackage(prospectInterestBillablesSnapshot?.[0]),
		accountServices,
		zoneId: zone?.id || '',
		moveInDate,
		hasLogin: owner?.hasLogin,
		coordinates: {
			latitude: address?.latitude,
			longitude: address?.longitude
		},
		emailId: owner?.emailId,
		ignoreOverdue,
		stickyNote,
		contractId: accountContract?.contractId,
		contractDateRange: accountContract?.contractStartDate ? `${formatDate(accountContract?.contractStartDate, MDY2)} - ${formatDate(accountContract?.contractEndDate, MDY2)}` : 'N/A',
		contractActive: accountContract?.contractActive,
		contractInstalledPrice: accountContract?.contractInstallPrice,
		staticIpsCount: accountContract?.staticIpsCount,
		voipNumbersCount: accountContract?.voipNumbersCount,
		equipment,
		isDelinquent,
		hasInvoices,
		tags: tags?.map(tag => ({
			id: tag.tagId?.toString(),
			name: tag.tagName
		})) || [],
		agreements: mapAccountAgreements(accountAgreements),
		hasDiscount
	}
}

const findPrimaryPhone = (phones: PhoneNumberApiResponse[] | undefined): { number: string
	extension: string } => {
	if (!phones) {
		return {
			number: '',
			extension: ''
		}
	}
	const primaryPhone = phones?.find((phone: PhoneNumberApiResponse) => phone.isPrimary)
	return {
		number: primaryPhone?.number || '',
		extension: primaryPhone?.extension || ''
	}
}

const mapAdditionalPhones = (additionalPhones: PhoneNumberApiResponse[] | undefined): AdditionalPhone[] => {
	if (!additionalPhones) {
		return []
	}
	return additionalPhones.filter(phone => !phone.isPrimary).map((phone): AdditionalPhone => {
		return {
			id: phone?.phoneId?.toString(),
			isPrimary: !!phone.isPrimary,
			number: phone.number,
			label: phone.label
		}
	})
}

const mapSignupPackage = (signupPackage: any): SignupPackage | null => {
	if (!signupPackage) {
		return null
	}
	const {
		billableId: id, billablePackageSpeed: speed, billablePrice: price, billableUnit: unit
	}: SignupPackageResponse = signupPackage
	return {
		id,
		speed,
		price,
		unit
	}
}

export { mapAccount, mapAdditionalPhones }
